import { Card, Col, Form } from "antd";
import React, { Fragment, useEffect, useMemo } from "react";
import { FieldPathValue, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { TemplateField } from "../../../types/template";
import { DateField, InputField, InputSwitch, ReactSelectField, TextAreaField } from "../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../atoms/FormElement/InputNumberField";
import { renderExtra } from "../../atoms/FormElement/renderExtra";
import {
  FormRules,
  getInputPlaceholderMessage,
  getSelectPlaceholderMessage,
  getValidationProps,
} from "../../libs/form";
import { isMarketingUrlFieldName } from "../../modules/Properties/PropertyDetail/Marketing/marketingUrlUtils";
import { DATE_FORMATS, isSelectOption } from "../../libs/utils";
import { UploadDocument } from "../../molecules/Documents";
import { CheckboxWrapper } from "./CheckBoxWrapper";

type DynamicFieldsProps<T extends FieldValues> = {
  template: TemplateField[];
  control: UseFormReturn<T>["control"];
  errors: UseFormReturn<T>["formState"]["errors"];
  setValue: UseFormReturn<T>["setValue"];
  colSpan?: number;
};

export const DynamicFields = <T extends FieldValues>(props: DynamicFieldsProps<T>) => {
  const { template, control, errors, setValue, colSpan = 12 } = props;

  const defaultsSignature = useMemo(
    () =>
      (template || [])
        .map((item) => `${item.name}:${JSON.stringify(item.default)}`)
        .join("|"),
    [template],
  );

  useEffect(() => {
    if (!template?.length) return;

    template.forEach((item) => {
      const fieldName = `dynamicFields.${item.name}` as Path<T>;
      const defaultValue = item?.default as FieldPathValue<T, typeof fieldName>;
      if (defaultValue !== undefined) {
        setValue(fieldName, defaultValue);
      }
    });
    // Re-hydrate only when field defaults change — not when isDisabled/label/options metadata updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsSignature, setValue]);

  return (
    <>
      {template.map((field, idx) => {
        const fieldName = `dynamicFields.${field.name}`;
        const isDisabled = Boolean(field?.isDisabled);
        const fieldColSpan = field.colSpan ?? colSpan;

        const fieldRules = (field.rules || {}) as Record<string, unknown>;
        const isRequired = Boolean(fieldRules.required);
        const { required: _required, ...restRules } = fieldRules;
        const rules = restRules as FormRules;

        switch (field.type) {
          case "number": {
            const { rules: numberRules, isRequired: numberRequired } = getValidationProps({
              fieldName,
              errors,
              isRequired,
              rules,
            });

            return (
              <Col key={idx + fieldName} span={fieldColSpan}>
                <InputNumberField
                  label={field.label}
                  fieldname={fieldName as Path<T>}
                  control={control}
                  disabled={isDisabled}
                  isRequired={numberRequired}
                  rules={numberRules}
                  formItemProps={{ extra: field.helperText }}
                  inputNumberProps={{
                    min: field.min !== undefined ? Number(field.min) : undefined,
                    max: field.max !== undefined ? Number(field.max) : undefined,
                    placeholder: getInputPlaceholderMessage(field.label),
                    precision: field.precision ?? 0,
                  }}
                />
              </Col>
            );
          }

          case "text":
          case "email":
            return (
              <Col key={idx + fieldName} span={fieldColSpan}>
                <InputField
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  setValue={setValue}
                  helpingText={field.helperText}
                  addonBefore={isMarketingUrlFieldName(field.name) ? "https://" : undefined}
                  iProps={{
                    type: field.type,
                    min: field.min,
                    max: field.max,
                    placeholder: getInputPlaceholderMessage(field.label),
                  }}
                  disabled={isDisabled}
                  {...getValidationProps({ fieldName, errors, isRequired, rules })}
                />
              </Col>
            );

          case "select":
            return (
              <Col key={idx + fieldName} span={fieldColSpan}>
                <ReactSelectField
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  setValue={setValue}
                  selectOption={
                    field.options?.map((opt) =>
                      isSelectOption(opt) ? { label: opt?.label, value: opt?.value } : { label: opt, value: opt },
                    ) || []
                  }
                  isDisabled={isDisabled}
                  isMulti={field.isMulti}
                  helpingText={field.helperText}
                  {...getValidationProps({ fieldName, errors, isRequired, rules })}
                  iProps={{ placeholder: getSelectPlaceholderMessage(field.label) }}
                />
              </Col>
            );

          case "file":
            return (
              <Col key={idx + fieldName} span={fieldColSpan}>
                <UploadDocument
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  disabled={isDisabled}
                  withPreview={false}
                  {...getValidationProps({ fieldName, errors, isRequired })}
                />
              </Col>
            );

          case "date":
            return (
              <Col key={idx + fieldName} span={fieldColSpan}>
                <DateField
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  setValue={setValue}
                  iProps={{ placeholder: DATE_FORMATS.display }}
                  disabled={isDisabled}
                  {...getValidationProps({ fieldName, errors, isRequired, rules })}
                />
              </Col>
            );

          case "textarea":
            return (
              <Col key={idx + fieldName} span={field.colSpan ?? 24}>
                <TextAreaField
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  setValue={setValue}
                  disabled={isDisabled}
                  iProps={{
                    placeholder: getInputPlaceholderMessage(field.label),
                    autoSize: { minRows: 4, maxRows: 8 },
                    maxLength: field.max === undefined ? undefined : Number(field.max),
                    showCount: field.max !== undefined,
                  }}
                  {...getValidationProps({ fieldName, errors, isRequired, rules })}
                />
              </Col>
            );

          case "checkbox":
            return (
              <Card key={idx + fieldName} title={field?.label || ""} style={{ marginInline: "0.5rem" }}>
                <CheckboxWrapper
                  parentLable={field?.label}
                  control={control}
                  idx={idx}
                  setValue={setValue}
                  options={field?.options as any}
                />
              </Card>
            );

          case "switch":
            return (
              <Col key={idx + fieldName} span={fieldColSpan}>
                <Form.Item label={field.label} style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: 32,
                      height: 32,
                    }}
                  >
                    <InputSwitch
                      isRequired={false}
                      fieldname={fieldName}
                      label=""
                      control={control}
                      valueGot={field?.default}
                      setValue={setValue}
                      disabled={isDisabled}
                    />
                  </div>
                  {field.helperText ? renderExtra(field.helperText) : null}
                </Form.Item>
              </Col>
            );

          default:
            return <Fragment key={idx + fieldName} />;
        }
      })}
    </>
  );
};
